import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import { useToast } from "../../../services/utils";
import { getTableColumns } from "../../../services/rulesService";
import ResourceColumnsChips from "../../../components/Organisms/ResourceColumnsChips";
import ConfirmDailog from "../../../components/Dialogs/ConfirmDailog";
import AvailableVariables from "../../../components/Molecules/Variables/AvailableVariables";
import QueryTemplatesCard from "../../../components/common/QueryTemplatesCard";
import {
  GENERIC_EXTERNAL_QUERY_TEMPLATES,
  GENERIC_RESOURCE_QUERY_TEMPLATES,
} from "../../../constants/queryTemplates";
import MixedSqlEditor from "./MixedSqlEditor";
import SubQueryCard from "./SubQueryCard";
import {
  IMixedSubQueryV2,
  buildResourceScopedColumns,
  buildExternalScopedColumns,
  buildMergeScopedColumns,
  buildMainQueryScopedColumns,
  MERGE_SUB_TYPES,
} from "./mixedQueryV2.utils";

interface Handlers {
  handleTypeChange: (index: number, value: string | null) => void;
  handleFieldChange: (index: number, field: string, value: any) => void;
  handleLinkedServiceChange: (index: number, field: string, value: any) => void;
  handleQueryChange: (index: number, value: string) => void;
  handleEditorFocus: (index: number) => void;
  handleEditorBlur: () => void;
  removeSubQuery: (index: number) => void;
  handleToggleActive: (index: number) => void;
  getDependencyDisplayLabel: (marker: string) => string;
  handleSetExternalColumns: (
    index: number,
    cols: { name: string; value: string }[],
  ) => void;
}

interface MixedQueryV2ContentProps {
  subQueries: IMixedSubQueryV2[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  columns: any;
  setColumns: React.Dispatch<React.SetStateAction<any>>;
  resourcesData: any[];
  linkedServicesData: any[];
  mainQuery: string;
  setMainQuery: (value: string) => void;
  handlers: Handlers;
  getSubQueryLabel: (item: IMixedSubQueryV2, idx: number) => string;
  onAddSource?: () => void;
  onExpand?: (index: number) => void;
  // Opens the standalone pop-out editor for a sub-query (index) or Main Query.
  onPopOut?: (index: number) => void;
  onMainQueryPopOut?: () => void;
  errors?: any;
  isReadOnly?: boolean;
  availableSubTypes?: string[];
}

// Presentational layout: data-source cards grid, an "Add source" card, and the
// Report Query section. Owns per-index editor refs so column chips insert into
// the active editor at the cursor (mirrors the legacy handleColumnSelect logic).
const MixedQueryV2Content: React.FC<MixedQueryV2ContentProps> = ({
  subQueries,
  activeIndex,
  setActiveIndex,
  columns,
  setColumns,
  resourcesData,
  linkedServicesData,
  mainQuery,
  setMainQuery,
  handlers,
  getSubQueryLabel,
  onAddSource,
  onExpand,
  onPopOut,
  onMainQueryPopOut,
  errors,
  isReadOnly = false,
  availableSubTypes,
}) => {
  const { fetchedConnectionKeys, editorRef: sharedEditorRef } =
    useRuleResourceContext();
  const { showToast } = useToast();
  const editorRefs = useRef<Record<number, React.MutableRefObject<any>>>({});
  const mainEditorRef = useRef<any>(null);
  const [fetchLoadingIndex, setFetchLoadingIndex] = React.useState<number>(-1);
  // Index of the sub-query pending deletion; drives the confirmation dialog.
  const [itemToDeleteIndex, setItemToDeleteIndex] = React.useState<
    number | null
  >(null);
  // Index of the sub-query pending deactivation; drives its confirmation dialog.
  const [itemToDeactivateIndex, setItemToDeactivateIndex] = React.useState<
    number | null
  >(null);

  const confirmRemoveSubQuery = () => {
    if (itemToDeleteIndex !== null) {
      handlers.removeSubQuery(itemToDeleteIndex);
    }
    setItemToDeleteIndex(null);
  };

  // Confirm before deactivating an active sub-query; activating is immediate.
  const requestToggleActive = (index: number) => {
    if (subQueries[index]?.is_active !== false) {
      setItemToDeactivateIndex(index);
    } else {
      handlers.handleToggleActive(index);
    }
  };

  const confirmDeactivateSubQuery = () => {
    if (itemToDeactivateIndex !== null) {
      handlers.handleToggleActive(itemToDeactivateIndex);
    }
    setItemToDeactivateIndex(null);
  };

  const ensureEditorRef = (index: number) => {
    if (!editorRefs.current[index]) {
      editorRefs.current[index] = { current: null };
    }
    return editorRefs.current[index];
  };

  // Insert a column at the cursor of the active editor (main or a sub-query).
  const handleColumnSelect = (column: string) => {
    const targetRef =
      activeIndex >= 0 ? editorRefs.current[activeIndex] : mainEditorRef;
    const editor =
      targetRef?.current?.editor ||
      mainEditorRef.current?.editor ||
      sharedEditorRef?.current?.editor;
    if (!editor) return;
    const pos = editor.getCursorPosition();
    const text = `"${column}"`;
    editor.session.insert(pos, text);
    editor.moveCursorToPosition({
      row: pos.row,
      column: pos.column + text.length,
    });
    editor.clearSelection();
    editor.focus();
  };

  // Insert $$VAR$$ at the cursor of the active editor (main or a sub-query),
  // mirroring the dialog's handleVariableSelect behaviour.
  const handleVariableSelect = (variable: string) => {
    const targetRef =
      activeIndex >= 0 ? editorRefs.current[activeIndex] : mainEditorRef;
    const editor =
      targetRef?.current?.editor ||
      mainEditorRef.current?.editor ||
      sharedEditorRef?.current?.editor;
    if (!editor) return;
    const pos = editor.getCursorPosition();
    const text = `$$${variable}$$ `;
    editor.session.insert(pos, text);
    editor.moveCursorToPosition({
      row: pos.row,
      column: pos.column + text.length,
    });
    editor.clearSelection();
    editor.focus();
  };

  const handleFetchColumns = async (index: number) => {
    const item = subQueries[index];
    if (
      !(item?.connection_key_id && item?.linked_service_id && item?.table_name)
    ) {
      showToast(
        "Please fill all external fields before fetching columns",
        "error",
      );
      return;
    }
    try {
      setFetchLoadingIndex(index);
      const payload = {
        linked_service_id: String(item?.linked_service_id),
        linked_service_code: item?.linked_service_code,
        connection_key_id: item?.connection_key_id,
        table_name: item?.table_name,
      };
      const response = await getTableColumns({ payload });
      if (response) {
        const rawColumns = (Object.values(response)[0] as string[]) ?? [];
        const formatted = rawColumns.map((col) => ({ name: col, value: col }));
        setColumns((prev: any) => ({
          ...prev,
          fetchedColumns: [
            { name: item?.table_name || "", columns: formatted },
          ],
        }));
        handlers.handleSetExternalColumns(index, formatted);
        showToast("Table columns fetched successfully", "success");
      } else {
        showToast("Cannot fetch table columns", "error");
      }
    } catch (err) {
      showToast("Cannot fetch table columns", "error");
    } finally {
      setFetchLoadingIndex(-1);
    }
  };

  // Context-scoped columns chips: Resource cards show only their resource's
  // columns; External cards show only their fetched table columns; comparison
  // sub-types show the rule-derived merged/missing/mismatched columns.
  const renderColumnsChipsFor = (item: IMixedSubQueryV2, index: number) => {
    const isExternal = item?.sub_type === "External";
    const isMerge = !!item?.sub_type && MERGE_SUB_TYPES.includes(item.sub_type);
    let scopedColumns: any;
    if (isMerge) {
      scopedColumns = buildMergeScopedColumns(item, columns);
    } else if (isExternal) {
      scopedColumns = buildExternalScopedColumns(item);
    } else {
      scopedColumns = buildResourceScopedColumns(item, columns);
    }
    return (
      <ResourceColumnsChips
        key={index}
        handleColumnSelect={handleColumnSelect}
        columns={scopedColumns}
        isFinalQuery={false}
        dialogType="mixed"
        queryBuilderType={
          isMerge
            ? "merged_query"
            : isExternal
              ? "external_query"
              : "resource_query"
        }
        mergeQueryType={
          isMerge ? item.sub_type : isExternal ? "External" : "Resource"
        }
      />
    );
  };

  // Main Query panel chips: aggregate ALL sub-queries.
  const mainQueryColumnsChips = (
    <ResourceColumnsChips
      handleColumnSelect={handleColumnSelect}
      columns={buildMainQueryScopedColumns(subQueries, columns)}
      isFinalQuery={false}
      dialogType="mixed"
      queryBuilderType="main_query"
      mergeQueryType="Resource"
    />
  );

  // Available Variables + Query Templates side panel (matches the dialog).
  const variablesPanel = (
    <>
      <Box className="available-box" sx={{ marginBottom: 1 }}>
        <AvailableVariables
          error={errors}
          setErrors={() => null}
          handleVariableSelect={handleVariableSelect}
        />
      </Box>
      <Box sx={{ marginBottom: 1 }}>
        <QueryTemplatesCard
          templates={[
            ...GENERIC_RESOURCE_QUERY_TEMPLATES,
            ...GENERIC_EXTERNAL_QUERY_TEMPLATES,
          ]}
          title="Query Templates"
        />
      </Box>
    </>
  );

  return (
    <Box className="mixed-v2-content">
      <Box className="mixed-v2-content__section-head">
        <Typography className="mixed-v2-content__section-title">
          Data Sources
        </Typography>
        <Typography className="mixed-v2-content__section-sub">
          Each source is a separate query pulling from a database, API, or file.
        </Typography>
      </Box>

      <Box className="mixed-v2-content__grid">
        {subQueries.map((item, index) => (
          <SubQueryCard
            key={item.id}
            item={item}
            index={index}
            errors={errors}
            isReadOnly={isReadOnly}
            resourcesData={resourcesData}
            linkedServicesData={linkedServicesData}
            connectionKeys={fetchedConnectionKeys || []}
            availableSubTypes={availableSubTypes}
            onTypeChange={handlers.handleTypeChange}
            onFieldChange={handlers.handleFieldChange}
            onLinkedServiceChange={handlers.handleLinkedServiceChange}
            onQueryChange={handlers.handleQueryChange}
            onEditorFocus={(i) => {
              setActiveIndex(i);
              handlers.handleEditorFocus(i);
            }}
            onEditorBlur={handlers.handleEditorBlur}
            onFetchColumns={handleFetchColumns}
            onRemove={(i) => setItemToDeleteIndex(i)}
            onToggleActive={requestToggleActive}
            onOpenEditor={onExpand}
            onPopOut={onPopOut}
            getDependencyDisplayLabel={handlers.getDependencyDisplayLabel}
            fetchColumnsLoading={fetchLoadingIndex === index}
            editorRef={ensureEditorRef(index)}
            columnsChipsSlot={renderColumnsChipsFor(item, index)}
            referenceLabel={getSubQueryLabel(item, index)}
          />
        ))}

        {onAddSource && (
          <Box
            className="mixed-v2-content__add-card"
            onClick={onAddSource}
            role="button"
          >
            <AddIcon />
            <Typography className="mixed-v2-content__add-title">
              Data Sources
            </Typography>
            <Typography className="mixed-v2-content__add-sub">
              Database, API, File, or any External Feed
            </Typography>
          </Box>
        )}
      </Box>

      <Box className="mixed-v2-content__report">
        <Box className="mixed-v2-content__report-head">
          <Box className="mixed-v2-content__report-head-text">
            <Typography className="mixed-v2-content__report-title">
              Main Query
            </Typography>
            <Typography className="mixed-v2-content__report-sub">
              Combines your data sources into the final report
            </Typography>
          </Box>
        </Box>
        <Box className="mixed-v2-content__report-body">
          <Box className="mixed-v2-content__report-editor">
            <MixedSqlEditor
              value={mainQuery}
              onChange={setMainQuery}
              editorStateKey="mixed-v2-main-query"
              label="Main Query"
              error={errors?.[-1] || {}}
              setErrors={() => null}
              onFocus={() => setActiveIndex(-1)}
              showAvailableVariables={false}
              showResourceColumnsChips={false}
              editorRef={mainEditorRef}
              onPopOut={onMainQueryPopOut}
            />
          </Box>
          <Box className="mixed-v2-content__report-side-panel">
            <Box className="mixed-v2-content__report-vars">
              {variablesPanel}
            </Box>
            <Box className="mixed-v2-content__report-columns">
              {mainQueryColumnsChips}
            </Box>
          </Box>
        </Box>
      </Box>

      <ConfirmDailog
        isDailogOpen={itemToDeleteIndex !== null}
        handleCancelAction={() => setItemToDeleteIndex(null)}
        handleConfirmAction={confirmRemoveSubQuery}
        dailogTitle="Remove Data Source"
        dailogDescription="Are you sure you want to remove this data source? This action cannot be undone."
      />

      <ConfirmDailog
        isDailogOpen={itemToDeactivateIndex !== null}
        handleCancelAction={() => setItemToDeactivateIndex(null)}
        handleConfirmAction={confirmDeactivateSubQuery}
        dailogTitle="Deactivate Data Source"
        dailogDescription="Are you sure you want to deactivate this data source? It will be excluded from the main query and other sub-queries' dependencies until reactivated."
      />
    </Box>
  );
};

export default MixedQueryV2Content;
